import mapboxgl from "mapbox-gl";

/**
 * `enableHighAccuracy` alone means every press demands a brand-new high-accuracy
 * fix and waits forever for it. `maximumAge` lets a recent fix satisfy the press
 * instantly (and survives a momentary provider hiccup); `timeout` stops a request
 * that the platform never answers from spinning indefinitely.
 */
export const GEOLOCATE_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 30000,
};

/** One retry is enough for a momentary provider failure; more just stalls the UI. */
export const MAX_TRANSIENT_RETRIES = 1;
export const TRANSIENT_RETRY_DELAY_MS = 1000;

export type GeolocateWatchState =
  | "OFF"
  | "WAITING_ACTIVE"
  | "ACTIVE_LOCK"
  | "ACTIVE_ERROR"
  | "BACKGROUND"
  | "BACKGROUND_ERROR";

export type GeolocateAction = "trigger" | "recenter" | "wait" | "reset-and-trigger";

/**
 * `GeolocateControl.trigger()` is a *toggle*, but our button means "show me where
 * I am". From WAITING_ACTIVE / ACTIVE_LOCK / ACTIVE_ERROR / BACKGROUND_ERROR a
 * plain `trigger()` turns tracking off and fires neither `geolocate` nor `error`,
 * so the caller's spinner never stops. Map each state to what the button should
 * actually do instead.
 */
export function nextGeolocateAction(watchState: GeolocateWatchState | undefined): GeolocateAction {
  switch (watchState) {
    // Nothing running, or tracking demoted to background: `trigger()` does the right thing.
    case "OFF":
    case "BACKGROUND":
    case undefined:
      return "trigger";
    // A fix is already in flight — pressing again should not cancel it.
    case "WAITING_ACTIVE":
      return "wait";
    // Already locked on the user; re-centre rather than switch tracking off.
    case "ACTIVE_LOCK":
      return "recenter";
    // The previous attempt failed. Toggle off to clear the stale watch, then start a fresh one.
    case "ACTIVE_ERROR":
    case "BACKGROUND_ERROR":
      return "reset-and-trigger";
    default:
      return "trigger";
  }
}

/**
 * POSITION_UNAVAILABLE is what macOS surfaces as `kCLErrorLocationUnknown` — the
 * platform could not get a fix *right now* (stale Wi-Fi scan, machine just woke,
 * no GPS yet). Apple documents it as transient, so it is worth one more attempt.
 */
export function isTransientPositionError(error: { code?: number } | undefined | null): boolean {
  return error?.code === 2;
}

export default function createGeoLocate() {
  const map = window.map;
  const geolocate = new mapboxgl.GeolocateControl({
    positionOptions: GEOLOCATE_POSITION_OPTIONS,
    trackUserLocation: true,
    showUserLocation: true,
    showAccuracyCircle: true,
  });

  let transientRetries = 0;
  let retryTimeoutId: number | undefined;

  const clearRetry = () => {
    if (retryTimeoutId !== undefined) {
      window.clearTimeout(retryTimeoutId);
      retryTimeoutId = undefined;
    }
  };

  // Handle successful geolocation
  geolocate.on("geolocate", () => {
    transientRetries = 0;
    clearRetry();
    window.geolocateLocated?.();
  });

  // Handle geolocation errors
  geolocate.on("error", (error) => {
    if (isTransientPositionError(error) && transientRetries < MAX_TRANSIENT_RETRIES) {
      transientRetries += 1;
      console.warn(
        `Location temporarily unavailable, retrying (${transientRetries}/${MAX_TRANSIENT_RETRIES})`,
        error
      );
      clearRetry();
      // Keep the caller's spinner running — this is not a final answer yet.
      retryTimeoutId = window.setTimeout(requestLocation, TRANSIENT_RETRY_DELAY_MS);
      return;
    }

    transientRetries = 0;
    clearRetry();
    console.error("Error getting location:", error);
    window.geolocateError?.(error);
  });

  /** Ask the control for a position without ever toggling tracking off. */
  const requestLocation = () => {
    switch (nextGeolocateAction(geolocate._watchState)) {
      case "wait":
        return;
      case "recenter": {
        const position = geolocate._lastKnownPosition;
        if (position) {
          map.easeTo({ center: [position.coords.longitude, position.coords.latitude] });
        }
        window.geolocateLocated?.();
        return;
      }
      case "reset-and-trigger":
        // The first call clears the errored watch (state -> OFF), the second starts a new one.
        geolocate.trigger();
        geolocate.trigger();
        return;
      default:
        geolocate.trigger();
    }
  };

  // Custom method to check permissions and trigger geolocation
  geolocate.permissionAndTrigger = () => {
    if (!("geolocation" in navigator)) {
      console.error("Geolocation is not available");
      window.geolocateError?.();
      alert("Geolocation is not available on your device. Please contact support@hiiker.co");
      return;
    }

    transientRetries = 0;
    clearRetry();

    // Not every browser can report geolocation permission state (older Safari throws).
    // The query is only an optimisation — when it is unavailable, let the browser prompt.
    if (!navigator.permissions?.query) {
      requestLocation();
      return;
    }

    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        if (result.state === "denied") {
          console.error("User denied permission to access location");
          window.geolocateError?.();
          alert("Please enable location services to use this feature");
          return;
        }
        // "granted" and "prompt" both go through trigger(); the browser raises the
        // permission prompt itself, so there is no need for a second position request.
        requestLocation();
      })
      .catch((error) => {
        console.warn("Permission query failed, requesting location anyway:", error);
        requestLocation();
      });
  };

  // Add the control to the map
  map.addControl(geolocate);

  // Hide the default button after the map loads
  const hideGeolocateButton = () => {
    const element = document.querySelector(".mapboxgl-ctrl-geolocate");
    if (element instanceof HTMLElement) {
      element.style.display = "none";
    } else {
      console.warn("Element not found in the DOM.");
    }
  };

  let mapRenderInstance = null;

  const mapRendered = () => {
    if (map.loaded()) {
      hideGeolocateButton();
      map.off("render", mapRenderInstance);
    }
  }

// Run if already loaded, or wait for load if not
  if (map.loaded()) {
    hideGeolocateButton();
  } else {
    map.on("load", () => {
      hideGeolocateButton();
    });
    mapRenderInstance = mapRendered;
    map.on("render", mapRenderInstance);
  }

  // Expose the geolocate control globally
  window.geoLocate = geolocate;
}
